import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  FileAddOutlined,
  PlusOutlined
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Alert, Button, Empty, Modal, Select, Spin, Tooltip, message } from "antd";
import dayjs from "dayjs";
import {
  deleteWarehouseDocument,
  fetchWarehouseDocuments,
  fetchWarehouseLookups,
  fetchWarehouseRestCheck,
  fillWarehouseInventory,
  postWarehouseDocument,
  unpostWarehouseDocument
} from "./warehouse-api";
import WarehouseDocumentDrawer from "./warehouse-document-drawer";
import WarehouseDocumentsFilters from "./warehouse-documents-filters";
import WarehouseShortageList from "./warehouse-shortage-list";
import refreshSrc from "../doc/plat/assets/refresh.svg";
import {
  PAGE_SIZE,
  formatDate,
  formatNumber,
  getLawFlag,
  normalizeDocument,
  normalizeLookups,
  normalizePage,
  normalizeRestCheck
} from "./warehouse-utils";
import "./warehouse.css";
import "./warehouse-documents.css";

const DEFAULT_FILTERS = {
  dateBeg: dayjs().startOf("month").format("YYYY-MM-DD"),
  dateEnd: dayjs().format("YYYY-MM-DD"),
  vid: undefined,
  idSkl: undefined,
  isAct: undefined,
  q: "",
  sortBy: "docdate",
  sortDir: "desc"
};

const VID_LABELS = { 1: "Приход", "-1": "Расход", 3: "Перемещение", 4: "Инвентаризация" };
const VID_VARIANTS = { 1: "in", "-1": "out", 3: "move", 4: "inventory" };

const SORT_OPTIONS = [
  { value: "docdate:desc", label: "Сначала новые" },
  { value: "docdate:asc", label: "Сначала старые" },
  { value: "syma:desc", label: "Сумма ↓" },
  { value: "syma:asc", label: "Сумма ↑" },
  { value: "idDoc:desc", label: "Номер ↓" }
];

const DocumentCard = ({ doc, vidLabel, selected, onClick }) => {
  const sf = [doc.sfDocnum && `СФ № ${doc.sfDocnum}`, doc.sfDocdate && `от ${formatDate(doc.sfDocdate)}`]
    .filter(Boolean)
    .join(" ");
  return (
    <button
      type="button"
      className={[
        "warehouse-doc-card",
        `warehouse-doc-card--${VID_VARIANTS[doc.vid] || "other"}`,
        selected ? "warehouse-doc-card--selected" : "",
        doc.isAct ? "warehouse-doc-card--posted" : ""
      ].filter(Boolean).join(" ")}
      onClick={onClick}
    >
      <div className="warehouse-doc-card__top">
        <span className="warehouse-doc-card__vid">{vidLabel}</span>
        <span className="warehouse-doc-card__num">#{doc.idDoc}</span>
        <span className="warehouse-doc-card__date">{formatDate(doc.docdate)}</span>
      </div>
      <div className="warehouse-doc-card__route">
        <span title={doc.fromName}>{doc.fromName || "—"}</span>
        <span title={doc.destName}>
          <span className="warehouse-doc-card__arrow">→</span>
          {doc.destName || "—"}
        </span>
      </div>
      <div className="warehouse-doc-card__meta" title={[sf, doc.komment].filter(Boolean).join(" · ")}>
        {[sf, doc.komment].filter(Boolean).join(" · ")}
      </div>
      <div className="warehouse-doc-card__bottom">
        <span className="warehouse-doc-card__sum">{formatNumber(doc.syma)}</span>
        <span className={`warehouse-doc-card__status${doc.isAct ? " warehouse-doc-card__status--posted" : ""}`}>
          {doc.isAct ? "Проведена" : "Черновик"}
        </span>
      </div>
    </button>
  );
};

const WarehouseDocuments = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: routeId } = useParams();
  const isNew = routeId === "new";
  const editId = routeId && /^\d+$/.test(routeId) ? Number(routeId) : null;
  const law = useSelector((state) => state.user.session?.law);
  const canView = getLawFlag(law, 90) === 1;
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [debouncedQ, setDebouncedQ] = useState("");
  const [lookups, setLookups] = useState({ warehouses: [], documentTypes: [] });
  const [rows, setRows] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const bodyRef = useRef(null);
  const requestRef = useRef(0);
  const skipRef = useRef(0);
  const hasMoreRef = useRef(false);
  const loadingMoreRef = useRef(false);
  const loadRef = useRef();

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQ(filters.q.trim()), 350);
    return () => clearTimeout(timer);
  }, [filters.q]);

  useEffect(() => {
    if (editId != null) setSelectedId(editId);
  }, [editId]);

  const closeDrawer = () => {
    navigate({ pathname: "/warehouse/documents", search: location.search });
  };

  useEffect(() => {
    if (!canView) return;
    fetchWarehouseLookups()
      .then((data) => setLookups(normalizeLookups(data)))
      .catch((e) => message.error(e.message || "Не удалось загрузить справочники склада"));
  }, [canView]);

  const loadPage = useCallback(
    async (reset) => {
      if (!reset && (loadingMoreRef.current || !hasMoreRef.current)) return;
      const requestId = ++requestRef.current;
      const skip = reset ? 0 : skipRef.current;
      if (reset) {
        setLoading(true);
        setError("");
        bodyRef.current?.scrollTo({ top: 0 });
      } else {
        loadingMoreRef.current = true;
        setLoadingMore(true);
      }
      try {
        const data = await fetchWarehouseDocuments({
          skip,
          take: PAGE_SIZE,
          sortBy: filters.sortBy,
          sortDir: filters.sortDir,
          dateBeg: filters.dateBeg,
          dateEnd: filters.dateEnd,
          vid: filters.vid,
          idSkl: filters.idSkl,
          isAct:
            filters.isAct === true ? 1 : filters.isAct === false ? 0 : undefined,
          query: debouncedQ
        });
        if (requestId !== requestRef.current) return;
        const page = normalizePage(data, normalizeDocument);
        setRows((previous) => (reset ? page.items : [...previous, ...page.items]));
        skipRef.current = skip + page.items.length;
        hasMoreRef.current = page.hasMore;
      } catch (e) {
        if (requestId !== requestRef.current) return;
        if (reset) {
          setRows([]);
          setError(e.message || "Не удалось загрузить накладные");
        } else message.error(e.message || "Не удалось подгрузить накладные");
      } finally {
        if (requestId === requestRef.current) {
          setLoading(false);
          setLoadingMore(false);
          loadingMoreRef.current = false;
        }
      }
    },
    [
      debouncedQ,
      filters.dateBeg,
      filters.dateEnd,
      filters.idSkl,
      filters.isAct,
      filters.sortBy,
      filters.sortDir,
      filters.vid
    ]
  );
  loadRef.current = loadPage;

  useEffect(() => {
    if (canView) loadPage(true);
  }, [canView, loadPage]);

  useEffect(() => {
    const body = bodyRef.current;
    if (!body) return undefined;
    const onScroll = () => {
      if (body.scrollTop + body.clientHeight >= body.scrollHeight - 120) loadRef.current?.(false);
    };
    body.addEventListener("scroll", onScroll);
    return () => body.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const body = bodyRef.current;
    if (!loading && body && hasMoreRef.current && body.scrollHeight <= body.clientHeight + 40) {
      loadRef.current?.(false);
    }
  }, [loading, loadingMore, rows.length]);

  const selected = rows.find((row) => row.idDoc === selectedId);
  const vidLabel = (value) =>
    lookups.documentTypes.find((item) => item.value === value)?.label ?? VID_LABELS[value] ?? value;

  const runAction = (title, action, success, options = {}) => {
    if (!selected) return;
    Modal.confirm({
      title,
      content: `Накладная #${selected.idDoc}`,
      okText: "Продолжить",
      cancelText: "Отмена",
      ...options,
      onOk: () =>
        action(selected.idDoc)
          .then(() => {
            message.success(success);
            setSelectedId(null);
            loadPage(true);
          })
          .catch((e) => {
            message.error(e.message || "Операция не выполнена");
            return Promise.reject(e);
          })
    });
  };

  const shortageList = (items) => <WarehouseShortageList idDoc={selected?.idDoc} items={items} />;

  const postSelected = async () => {
    if (!selected) return;
    let check;
    try {
      check = normalizeRestCheck(await fetchWarehouseRestCheck(selected.idDoc));
    } catch (e) {
      message.error(e.message || "Не удалось проверить остатки");
      return;
    }
    if (!check.items.length) {
      runAction("Провести накладную?", postWarehouseDocument, "Накладная проведена");
    } else if (check.mode === 2) {
      Modal.error({ title: "Проведение запрещено", content: shortageList(check.items), okText: "Закрыть" });
    } else {
      runAction("Остаток уйдёт в минус", postWarehouseDocument, "Накладная проведена", {
        content: shortageList(check.items),
        okText: "Провести всё равно",
        okButtonProps: { danger: true }
      });
    }
  };

  if (!canView) {
    return <Alert type="warning" showIcon message="Нет права просмотра накладных (LAW[90])" />;
  }

  return (
    <div className="warehouse-page warehouse-documents">
      <WarehouseDocumentsFilters
        value={filters}
        documentTypes={lookups.documentTypes}
        warehouses={lookups.warehouses}
        onChange={setFilters}
      >
        <Tooltip title="Добавить">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate({ pathname: "/warehouse/documents/new", search: location.search })}
          />
        </Tooltip>
        <Tooltip title="Удалить">
          <Button
            danger
            icon={<DeleteOutlined />}
            disabled={!selected}
            onClick={() => runAction("Удалить накладную?", deleteWarehouseDocument, "Накладная удалена")}
          />
        </Tooltip>
        <Tooltip title={selected?.isAct ? "Отменить проведение" : "Провести"}>
          <Button
            icon={selected?.isAct ? <CloseCircleOutlined /> : <CheckCircleOutlined />}
            disabled={!selected}
            onClick={() =>
              selected?.isAct
                ? runAction("Отменить проведение?", unpostWarehouseDocument, "Проведение отменено")
                : postSelected()
            }
          />
        </Tooltip>
        <Tooltip title="Заполнить инвентаризацию">
          <Button
            icon={<FileAddOutlined />}
            disabled={!selected || selected.isAct || selected.vid !== 4}
            onClick={() =>
              runAction("Заполнить остатками склада?", fillWarehouseInventory, "Инвентаризация заполнена")
            }
          />
        </Tooltip>
        <Select
          className="warehouse-documents__sort"
          value={`${filters.sortBy}:${filters.sortDir}`}
          options={SORT_OPTIONS}
          popupMatchSelectWidth={false}
          onChange={(next) => {
            const [sortBy, sortDir] = next.split(":");
            setFilters((previous) => ({ ...previous, sortBy, sortDir }));
          }}
        />
        <button
          type="button"
          className="warehouse-documents__icon-btn"
          disabled={loading}
          title="Обновить"
          aria-label="Обновить"
          onClick={() => loadPage(true)}
        >
          <span className="warehouse-documents__icon-btn-icon">
            <img src={refreshSrc} alt="" />
          </span>
        </button>
      </WarehouseDocumentsFilters>
      {error ? <Alert type="error" showIcon message={error} className="warehouse-documents__error" /> : null}
      <div className="warehouse-documents__body">
        <div className="warehouse-documents__cards-wrap" ref={bodyRef}>
          <Spin spinning={loading}>
            {rows.length ? (
              <div className="warehouse-documents__cards">
                {rows.map((doc) => (
                  <DocumentCard
                    key={doc.idDoc}
                    doc={doc}
                    vidLabel={vidLabel(doc.vid)}
                    selected={doc.idDoc === selectedId}
                    onClick={() => {
                      setSelectedId(doc.idDoc);
                      navigate({ pathname: `/warehouse/documents/${doc.idDoc}`, search: location.search });
                    }}
                  />
                ))}
              </div>
            ) : !loading ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Накладных нет" />
            ) : (
              <div className="warehouse-documents__cards-placeholder" />
            )}
            {loadingMore ? <div className="warehouse-documents__more"><Spin size="small" /></div> : null}
          </Spin>
        </div>
      </div>
      <WarehouseDocumentDrawer
        open={isNew || editId != null}
        id={editId}
        lookups={lookups}
        onClose={closeDrawer}
        onSaved={() => {
          closeDrawer();
          loadPage(true);
        }}
        onChanged={(saved) => {
          loadPage(true);
          if (saved?.idDoc != null && saved.idDoc !== editId) {
            navigate({ pathname: `/warehouse/documents/${saved.idDoc}`, search: location.search }, { replace: true });
          }
        }}
      />
    </div>
  );
};

export default WarehouseDocuments;
